import { IGeneralModule, IModule } from "./Interface";
import { ProductConfigurationStore } from "../Stores/ProductConfigurationStore";
import { compact } from "lodash";
import { generalConfigurationStore } from "../Stores/GeneralConfigurationStore";
import { Options } from "../config";

const utilsFunctions = {
	shouldOverwriteImageOfModule(module: IModule | IGeneralModule, productConfigurationStore: ProductConfigurationStore) {
		return productConfigurationStore.selection.deliverType?.dependentModules?.find((dm) => dm.id === module.id)?.configImage;
	},

	getRenderImages(pC: ProductConfigurationStore) {
		const baseImage = pC.selection.deliverType?.configImage;

		const stepImages =
			pC.modules?.steps?.flatMap((step) => {
				return pC.getSelectedMainModulesOfStep(step).flatMap((module) => {
					const moduleImage = this.shouldOverwriteImageOfModule(module, pC) || module.configImage;
					return pC
						.getSelectedSubModulesOfModuleInStep(module, step)
						.map((subModule) => {
							return this.shouldOverwriteImageOfModule(subModule, pC) || subModule.configImage;
						})
						.concat([moduleImage]);
				});
			}) || [];

		const generalModuleImages =
			pC.modules?.generalStep?.moduleTypes?.flatMap((moduleType) => {
				return pC.getSelectedGeneralModulesOfModuleType(moduleType).flatMap((module) => {
					const moduleImage = this.shouldOverwriteImageOfModule(module, pC) || module.configImage;
					return pC
						.getSelectedGeneralSubModulesOfGeneralModuleInModuleType(module, moduleType)
						.map((subModule) => {
							return this.shouldOverwriteImageOfModule(subModule, pC) || subModule.configImage;
						})
						.concat([moduleImage]);
				});
			}) || [];

		const mergedArray = stepImages.concat(generalModuleImages);
		mergedArray.push(baseImage);

		const concatArrays = compact(stepImages.concat(generalModuleImages).concat([baseImage]))

		const sortedArray = concatArrays.sort(function (n1, n2) {
			if (n1.level == null && n2.level == null) {
				return 0;
			}
			if (n1.level == null) {
				return -1;
			}
			if (n2.level == null) {
				return 1;
			}
			if (n1.level && n2.level) {
				const level1 = parseInt(n1.level);
				const level2 = parseInt(n2.level);
				// ascending sort
				return level1 - level2;
			} else {
				return 0;
			}
		});

		// sortedArray.forEach(elem => console.log("sorted level", elem.level, elem.url));
		// console.log("sorted array finished", compact(sortedArray.map((image) => image?.url)));

		return compact(sortedArray.map((image) => image?.url))
	},

	getAnchor(headline: string | undefined) {
		if (headline) {
			headline = headline.toLowerCase().replace(/[^a-zA-Z0-9]+/g, "");
		}
		return headline;
	},

	getReadableSelectedModules(p: ProductConfigurationStore) {
		const title = compact([p.product?.title?.rendered || ""].concat([p.product?.productBase?.sku || ""]).concat("\n"));

		const steps = compact(
			p.modules?.steps?.flatMap((step, index) => {
				const modules =
					p.selection.modules[step.stepId]?.map((module) => {
						return `${module.title} (${module.moduleFields?.mrModuleSku})`;
					}) || [];

				if (modules.length === 0) {
					modules.push(" - ");
				}

				return [(step.stepHeadline || index) + ":"].concat(modules).concat("\n");
			})
		);

		const generalModules = compact(
			p.modules?.generalStep?.moduleTypes?.flatMap((moduleType, index) => {
				const modules =
					p.selection.generalModules[moduleType.termId]?.map((module) => {
						return `${module.title} (${module.moduleFields?.mrModuleSku})`;
					}) || [];

				if (modules.length === 0) {
					modules.push(" - ");
				}

				return [(moduleType.termName || index) + ":"].concat(modules).concat("\n");
			})
		);

		const coulterWidths = [generalConfigurationStore.configs?.detailsStepSubheadlineCoulterWidths ? generalConfigurationStore.configs?.detailsStepSubheadlineCoulterWidths + ":" : ""]
			.concat(
				compact(
					p.selection.options[Options.coulterWidths]?.flatMap((options) => {
						return options.name ? options.name : "";
					})
				)
			)
			.concat(p.selection.options[Options.coulterWidths] ? "\n" : "");

		const rowWidths = [generalConfigurationStore.configs?.detailsStepSubheadlineRowWidths ? generalConfigurationStore.configs?.detailsStepSubheadlineRowWidths + ":" : ""]
			.concat(
				compact(
					p.selection.options[Options.rowWidths]?.flatMap((options) => {
						return options.name ? options.name : "";
					})
				)
			)
			.concat(p.selection.options[Options.rowWidths] ? "\n" : "");

		const plantation = [generalConfigurationStore.configs?.detailsStepSubheadlinePlantation ? generalConfigurationStore.configs?.detailsStepSubheadlinePlantation + ":" : ""]
			.concat(
				compact(
					p.selection.options[Options.plannedPlantations]?.flatMap((options) => {
						return options.name ? options.name : "";
					})
				)
			)
			.concat(p.selection.options[Options.plannedPlantations] ? "\n" : "");

		const trackWidth = [generalConfigurationStore.configs?.detailsStepSubheadlineTrackWidth ? generalConfigurationStore.configs?.detailsStepSubheadlineTrackWidth + ":" : ""]
			.concat([p.selection.textNotes[Options.trackWidth] ? p.selection.textNotes[Options.trackWidth] : ""])
			.concat(p.selection.textNotes[Options.trackWidth] ? "\n" : "");

		const customerNote = [generalConfigurationStore.configs?.cultivationTypeSubheadlineNote ? generalConfigurationStore.configs?.cultivationTypeSubheadlineNote + ":" : ""]
			.concat([p.selection.textNotes[Options.customerAnnotation] ? p.selection.textNotes[Options.customerAnnotation] : ""])
			.concat(p.selection.textNotes[Options.customerAnnotation] ? "\n" : "");

		const deliverType = [(p.modules?.deliverType?.deliverTypesHeadline ? p.modules?.deliverType?.deliverTypesHeadline + ":" : "")]
			.concat([p.selection.deliverType?.name ? p.selection.deliverType?.name : ""])
			.concat(p.selection.deliverType?.name ? "\n" : "");

		return title
			.concat(steps)
			.concat(generalModules)
			.concat(deliverType)
			.concat(coulterWidths)
			.concat(rowWidths)
			.concat(plantation)
			.concat(trackWidth)
			.concat(customerNote);
	},
};

export default utilsFunctions;
